'use client'

import {
  CardGrid,
  Container,
  GlassCard,
  GlassCardBody,
  Reveal,
  Section,
  SectionHeader,
  Stagger,
} from 'design-studio'
import {
  SL_GLASS_BODY,
  SL_GLASS_SURFACE,
  SL_GLASS_TITLE,
} from '@/components/theme'

const USE_CASES = [
  {
    title: 'Discover test gaps and uncovered behavior',
    description:
      'Identify meaningful behaviors that are not covered by current scenarios or tests, and generate higher-value tests from uncovered regions.',
  },
  {
    title: 'Discover hidden paths before migration',
    description:
      'Use mathematical context to understand legacy behavior, preserve critical properties, and reduce risk while moving to new implementations.',
  },
  {
    title: 'Discover change impact before merge',
    description:
      'Reason about how a proposed edit changes reachable behavior, assumptions, and edge cases before code is merged.',
  },
  {
    title: 'Discover policy conflicts and exception paths',
    description:
      'Model complex business rules, exception paths, and constraints so AI agents can reason about compliance-sensitive behavior with more rigor.',
  },
  {
    title: 'Discover drift across requirements, tests, and logs',
    description:
      'Connect intended behavior, tested behavior, and observed production behavior through a shared model to find drift, contradictions, and missing cases.',
  },
  {
    title: 'Connect observability to system semantics',
    description:
      'Use logs, traces, and runtime evidence to refine the discovered model, understand which paths are actually exercised in production, and identify unexpected or drifting behavior.',
  },
]

export function SectionUseCases() {
  return (
    <Section
      id="use-cases"
      className="relative scroll-mt-19 py-16 sm:pt-24 sm:pb-32"
    >
      <Container className="relative z-10">
        <Stagger className="space-y-10">
          <Reveal className="mx-auto max-w-6xl">
            <SectionHeader
              pretitle="What you can discover"
              pretitleColor="text-teal-500"
              title="Discover Hidden Paths, Gaps and Change Effects"
              description="SpecLogician helps developers and AI agents discover behavioral structure that is hard to see from local code alone — then use it for testing, migration, change review, policy-heavy engineering, and semantic observability."
            />
          </Reveal>

          <Reveal className="mx-auto max-w-7xl">
            <CardGrid columns={3} gap="sm">
              {USE_CASES.map((item) => (
                <GlassCard
                  key={item.title}
                  title={item.title}
                  titleClassName={SL_GLASS_TITLE}
                  className={SL_GLASS_SURFACE}
                >
                  <GlassCardBody className={SL_GLASS_BODY}>
                    {item.description}
                  </GlassCardBody>
                </GlassCard>
              ))}
            </CardGrid>
          </Reveal>
        </Stagger>
      </Container>
    </Section>
  )
}
